import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Spin, Card, Tag, Breadcrumb, Row, Col, Space, Divider, Modal, Form, Input, Avatar, Descriptions, Tabs } from "antd";
import { all_routes } from "../../../router/all_routes";
import { getVacationIntimationDetails, getAdminVacationIntimationDetails, getVacationRequests, approveRejectVacationIntimation, downloadVacationLetter, approveRejectStaffQuarterFinance } from "../../../../core/data/redux/staffQuarterSlice";
import ApproverTracker from "../components/ApproverTracker";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { decodeRouteParam } from "../../../../core/hooks/routeEncoder";
import { formatFlatNoWithBlock, resolveFlatCategory } from "../staffQuarterUtils";
import { readEmployeeVacationIntimations, findVacationIntimationIndex } from "./vacationIntimationData";
import { getMyProfile } from "../../../../core/data/redux/employeesSlice";

const VacationDetails = () => {
    const { id: encodedId } = useParams<{ id: string }>();
    const id = decodeRouteParam(encodedId || "");
    const [searchParams] = useSearchParams();
    const employeeId = searchParams.get("empId") || "";
    const isVacationRequest = searchParams.get("type") === "request";
    const allotmentReferenceNumber = searchParams.get("allotmentReferenceNumber") || id;
    const [employeeData, setEmployeeData] = useState<any>(null);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [employeeLoading, setEmployeeLoading] = useState(!!employeeId);
    const [employeeError, setEmployeeError] = useState("");
    const [reload, setReload] = useState(0);
    const navigate = useNavigate();
    const dispatch = useDispatch<any>();
    const routes = all_routes as any;

    const staffQuarterState = useSelector((state: any) => state.staffQuarterState);
    const profile = useSelector((state: any) => state.employeesState?.data?.data?.details || state.employeesState?.data?.data || state.employeesState?.myProfile || {});
    const rawData = staffQuarterState?.vacationDetails?.data;
    const requestPayload = staffQuarterState?.vacationRequests;
    const requestItems = [requestPayload, requestPayload?.data, requestPayload?.data?.data, requestPayload?.items, requestPayload?.data?.items]
        .find(Array.isArray) || [];
    const requestData = requestItems.find((item: any) => [item.vacationId, item.id, item.allotmentReferenceNumber, item.registrationReferenceNumber]
        .some(value => value != null && String(value) === String(id))) || requestItems[0];
    const apiData = employeeId ? employeeData?.items?.[selectedIndex] : isVacationRequest ? requestData : Array.isArray(rawData)
        ? rawData.find((item: any) => String(item.allotmentReferenceNumber) === String(id) || String(item.vacationId) === String(id) || String(item.registrationReferenceNumber) === String(id)) || rawData[0]
        : rawData;
    const isLoading = employeeId ? employeeLoading : staffQuarterState.loading === "pending";

    const adminAuth = useSelector((state: any) => state.adminAuthState);
    const user = adminAuth?.user?.data?.data?.user || JSON.parse(localStorage.getItem("user") || "{}");
    const isAdmin = user?.role?.toUpperCase() === "ADMIN";

    const ensureBase64 = (img: string | undefined) => {
        if (!img) return undefined;
        if (img.startsWith("data:") || img.startsWith("http")) return img;
        // If it's a long string without dots/spaces, it's likely raw base64
        if (img.length > 50 && !img.includes(".") && !img.includes(" ")) {
            return `data:image/jpeg;base64,${img}`;
        }
        return img;
    };

    const currentUserId = (user?.empId || user?.employeeNumber || user?.employee_number || user?.id || "").toString().replace(/^0+/, '');
    const dataUserId = (apiData?.empId || apiData?.registration?.employeeNumber || "").toString().replace(/^0+/, '');

    const approversList = [apiData?.approvers, apiData?.approversConfig, apiData?.approvalDetails, apiData?.approvalHistory, apiData?.approvalFlow]
        .find(Array.isArray) || [];
    const employeeName = apiData?.empName || apiData?.employeeName || employeeData?.empName || apiData?.registration?.employeeName || profile?.empName || profile?.employeeName || profile?.name || profile?.fullName || "DMRC Employee";
    const employeeNumber = apiData?.empId || apiData?.employeeId || employeeData?.empId || profile?.empId || profile?.employeeNumber || profile?.emp_no || "-";
    const employeeDesignation = apiData?.empDesignationName || apiData?.designation || employeeData?.empDesignationName || profile?.designation || profile?.designationName || "Staff Member";
    const employeeImage = apiData?.empImage || employeeData?.empImage || profile?.image || profile?.empImage || profile?.profileImage;

    // Check if current user is the assigned approver for the current pending step
    const currentApprover = approversList.find((a: any) => {
        const apprId = (a.approverId || "").toString().replace(/^0+/, '');
        return a.status?.toUpperCase() === "PENDING" &&
            (a.isAssigned === "1" || a.isAssigned === true || a.isAssigned === 'true') &&
            apprId === currentUserId;
    });
    const isCurrentApprover = !!currentApprover;

    // Check if current user is any associated approver (past, present, or future) in the flow
    const isAssociatedApprover = approversList.some((a: any) => {
        const apprId = (a.approverId || "").toString().replace(/^0+/, '');
        return apprId === currentUserId;
    });

    // Finance Details Data
    const financeObj = apiData?.vacationFinanceDetails || apiData?.financeDetails || apiData?.finance_details || apiData?.finance || apiData?.financeRequest || null;
    const recoveryDetailsObj = financeObj?.recoveryDetails || financeObj?.recovery_details || apiData?.recoveryDetails || apiData?.recovery_details || {};
    const rawRecoveryAmount = recoveryDetailsObj?.recoveryAmount ?? financeObj?.recoveryAmount ?? apiData?.recoveryAmount;
    const rawShortfallDays = recoveryDetailsObj?.shortfallDays ?? recoveryDetailsObj?.periodOfShortfallDays ?? recoveryDetailsObj?.periodOfShortfall ?? financeObj?.shortfallDays ?? apiData?.shortfallDays;

    const isVacationApproved = ["APPROVED", "CONFIRMED", "COMPLETED", "ACCEPTED", "VACATED"].includes(
        String(apiData?.status || apiData?.approvalStatus || apiData?.currentStatus || apiData?.vacationStatus || apiData?.requestStatus || (apiData?.isApproved ? "APPROVED" : "PENDING")).toUpperCase()
    );
    const hasVacationFinanceApiData = !!(apiData?.vacationFinanceDetails || apiData?.financeDetails || apiData?.finance_details || apiData?.finance);

    const hasFinanceData = isVacationApproved && hasVacationFinanceApiData && !!financeObj && !!(
        financeObj.id ||
        financeObj.status ||
        financeObj.vacationDate ||
        (Array.isArray(financeObj.approvals) && financeObj.approvals.length > 0)
    );

    const financeApproversList = [financeObj?.approvals, financeObj?.approverDetails, financeObj?.approvers].find(Array.isArray) || [];

    const currentFinanceApprover = financeApproversList.find((a: any) => {
        const apprId = (a.approverId || a.empId || "").toString().replace(/^0+/, '');
        return String(a.status || "").toUpperCase() === "PENDING" &&
            ([true, 1, "1", "true"].includes(a.isAssigned) || a.isAssigned === undefined) &&
            apprId === currentUserId;
    });

    const isCurrentFinanceApprover = !!currentFinanceApprover || (
        !!financeObj &&
        String(financeObj.status || "").toUpperCase() === "PENDING" &&
        ((financeObj.approverEmpId || financeObj.assignedTo || "").toString().replace(/^0+/, '') === currentUserId)
    );

    const trackerFinanceApprovers = hasFinanceData ? [
        {
            id: String(employeeNumber),
            empId: String(employeeNumber),
            name: employeeName,
            status: "Submitted" as const,
            stepName: "Vacation Requested",
            image: ensureBase64(employeeImage)
        },
        ...financeApproversList.map((appr: any, idx: number) => {
            const apprStatus = String(appr.status || "PENDING").toUpperCase();
            return {
                id: String(appr.approverId || appr.id || idx),
                empId: String(appr.approverId || appr.empId || idx),
                name: appr.approverName || `Finance Approver L${idx + 1}`,
                image: ensureBase64(appr.approverImage),
                status: apprStatus === "APPROVED" ? ("Approved" as const) : apprStatus === "REJECTED" ? ("Rejected" as const) : ("Pending" as const),
                stepName: appr.approvedByRole || `Finance L${idx + 1}`,
            };
        })
    ] : [];

    const pendingFinanceApprover = financeApproversList.find((appr: any) =>
        String(appr.status || "").toUpperCase() === "PENDING" && [true, 1, "1", "true"].includes(appr.isAssigned)
    ) || financeApproversList.find((appr: any) => String(appr.status || "").toUpperCase() === "PENDING");

    const trackerApprovers = isVacationRequest ? [
        { id: String(employeeNumber), empId: String(employeeNumber), name: employeeName, status: "Submitted" as const, stepName: "Vacation Requested", image: ensureBase64(employeeImage) },
        ...approversList.map((approver: any) => ({
            id: approver.approverId,
            empId: approver.approverId,
            name: approver.approverName || `Approver L${approver.approvalLevel}`,
            image: ensureBase64(approver.approverImage),
            status: String(approver.status || "PENDING").toUpperCase() === "APPROVED" ? "Approved" as const : String(approver.status || "PENDING").toUpperCase() === "REJECTED" ? "Rejected" as const : "Pending" as const,
            stepName: `L${approver.approvalLevel} Approval`,
        })),
    ] : [];
    const pendingApprover = approversList.find((approver: any) => String(approver.status || "").toUpperCase() === "PENDING" && [true, 1, "1", "true"].includes(approver.isAssigned)) ||
        approversList.find((approver: any) => String(approver.status || "").toUpperCase() === "PENDING") ||
        apiData?.activeApproval || apiData?.nextApprover || apiData?.pendingApprover;

    const getVacationIntimationDate = () => {
        // Priority 1: Direct key lastRequestVacationIntimationDate
        if (apiData?.lastRequestVacationIntimationDate) return apiData.lastRequestVacationIntimationDate;
        if (apiData?.lastRequest?.vacationIntimationDate) return apiData.lastRequest.vacationIntimationDate;
        if (employeeData?.lastRequestVacationIntimationDate) return employeeData.lastRequestVacationIntimationDate;
        if (employeeData?.lastRequest?.vacationIntimationDate) return employeeData.lastRequest.vacationIntimationDate;
        if (financeObj?.lastRequestVacationIntimationDate) return financeObj.lastRequestVacationIntimationDate;

        // Priority 2: Look up items in employeeData for lastRequestVacationIntimationDate or vacationIntimationDate
        if (employeeData?.items && Array.isArray(employeeData.items)) {
            const itemWithLastReq = employeeData.items.find((it: any) => it.lastRequestVacationIntimationDate || it.lastRequest?.vacationIntimationDate);
            if (itemWithLastReq?.lastRequestVacationIntimationDate) return itemWithLastReq.lastRequestVacationIntimationDate;
            if (itemWithLastReq?.lastRequest?.vacationIntimationDate) return itemWithLastReq.lastRequest.vacationIntimationDate;

            const intimatedItem = employeeData.items.find((it: any) =>
                (String(it.status || "").toUpperCase() === "INTIMATED" || it.vacationIntimationId === "VI-2026-003" || String(it.id) === "16") &&
                (it.vacationIntimationDate || it.vacation_intimation_date)
            );
            if (intimatedItem?.vacationIntimationDate) return intimatedItem.vacationIntimationDate;
            if (intimatedItem?.vacation_intimation_date) return intimatedItem.vacation_intimation_date;
        }

        // Priority 3: Fallback keys
        return (
            apiData?.vacationIntimationDate ||
            apiData?.vacation_intimation_date ||
            financeObj?.vacationIntimationDate ||
            null
        );
    };

    const detailsTitle = isVacationRequest ? "Vacation Request Details" : "Vacation Intimation Details";
    const backTab = isVacationRequest ? "REQUEST_VACATION" : "INTIMATION_FORM";

    const [isActionModalOpen, setIsActionModalOpen] = useState(false);
    const [actionType, setActionType] = useState<"Approve" | "Reject" | null>(null);
    const [form] = Form.useForm();

    const [isFinanceModalOpen, setIsFinanceModalOpen] = useState(false);
    const [financeActionType, setFinanceActionType] = useState<"Approve" | "Reject" | null>(null);
    const [financeForm] = Form.useForm();

    const [activeTabKey, setActiveTabKey] = useState<string>("vacation");

    useEffect(() => {
        dispatch(getMyProfile({}));
    }, [dispatch]);

    useEffect(() => {
        if (employeeId) {
            let active = true;
            setEmployeeLoading(true);
            setEmployeeError("");
            setEmployeeData(null);
            dispatch(getVacationIntimationDetails({ empId: employeeId })).unwrap()
                .then((response: any) => {
                    if (!active) return;
                    const data = readEmployeeVacationIntimations(response);
                    setEmployeeData(data);
                    setSelectedIndex(findVacationIntimationIndex(data, id));
                })
                .catch((error: any) => { if (active) setEmployeeError(typeof error === "string" ? error : error?.message || "Unable to load vacation intimations."); })
                .finally(() => { if (active) setEmployeeLoading(false); });
            return () => { active = false; };
        }
        if (isVacationRequest) {
            dispatch(getVacationRequests({ allotmentReferenceNumber, vacationId: id }));
            return;
        }
        if (id) {
            if (isAdmin) {
                dispatch(getAdminVacationIntimationDetails({ id }));
            } else {
                dispatch(getVacationIntimationDetails({ id }));
            }
        }
    }, [dispatch, id, employeeId, isAdmin, reload, isVacationRequest, allotmentReferenceNumber]);

    const handleAction = (values: any) => {
        const payload = {
            vacationIntimationId: apiData?.vacationId || apiData?.id,
            action: actionType === "Approve" ? "APPROVED" : "REJECTED",
            remarks: values.remarks
        };

        dispatch(approveRejectVacationIntimation(payload)).then((res: any) => {
            if (res.payload?.success || res.payload?.status === 200) {
                toast.success(`Request ${actionType}d successfully!`);
                setIsActionModalOpen(false);
                if (employeeId) {
                    setReload(value => value + 1);
                } else if (isVacationRequest) {
                    dispatch(getVacationRequests({ allotmentReferenceNumber, vacationId: id }));
                } else if (isAdmin) {
                    dispatch(getAdminVacationIntimationDetails({ id }));
                } else {
                    dispatch(getVacationIntimationDetails({ allotmentReferenceNumber: id }));
                }
            } else {
                toast.error(res.payload?.message || `Failed to ${actionType} request`);
            }
        });
    };

    const handleFinanceActionSubmit = (values: any) => {
        const payload: any = {
            financeId: financeObj?.id || apiData?.id,
            action: financeActionType === "Approve" ? "APPROVED" : "REJECTED",
            remarks: values.remarks || ""
        };
        if (values.licenseType) payload.licenseType = values.licenseType;
        if (values.licenseFees) payload.licenseFees = Number(values.licenseFees);
        if (values.hraAmount) payload.hraAmount = Number(values.hraAmount);

        dispatch(approveRejectStaffQuarterFinance(payload)).then((res: any) => {
            if (res.payload?.success || res.payload?.status === 200 || res.payload?.message) {
                toast.success(`Finance request ${financeActionType === "Approve" ? "approved" : "rejected"} successfully!`);
                setIsFinanceModalOpen(false);
                financeForm.resetFields();
                if (employeeId) {
                    setReload(v => v + 1);
                } else if (isVacationRequest) {
                    dispatch(getVacationRequests({ allotmentReferenceNumber, vacationId: id }));
                } else if (isAdmin) {
                    dispatch(getAdminVacationIntimationDetails({ id }));
                } else {
                    dispatch(getVacationIntimationDetails({ allotmentReferenceNumber: id }));
                }
            } else {
                toast.error(res.payload?.message || `Failed to process finance request`);
            }
        });
    };

    const handleDownloadVacationLetter = () => {
        const vacId = apiData?.id || apiData?.vacationId || apiData?.allotmentId || id || 12;
        const regId = apiData?.registrationId || apiData?.id || 45;

        toast.info("Downloading Vacation Letter...");
        dispatch(downloadVacationLetter({ vacationId: vacId, registrationId: regId }))
            .then((res: any) => {
                if (res.payload) {
                    const blob = new Blob([res.payload], { type: "application/pdf" });
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement("a");
                    link.href = url;
                    link.download = `Vacation_Letter_${vacId}.pdf`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);
                    toast.success("Vacation Letter downloaded successfully!");
                } else {
                    toast.error("Failed to download Vacation Letter PDF.");
                }
            })
            .catch(() => {
                toast.error("Error downloading Vacation Letter PDF.");
            });
    };

    const getStatusColor = (status: string) => {
        if (!status) return "warning";
        const statusUpper = String(status).toUpperCase();
        if (["APPROVED", "CONFIRMED", "COMPLETED", "ACCEPTED", "VACATED"].includes(statusUpper)) return "success";
        if (["REJECTED", "DECLINED", "CANCELLED"].includes(statusUpper)) return "error";
        if (["INTIMATED", "NOTICE"].includes(statusUpper)) return "processing";
        return "warning";
    };

    if (isLoading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: "70vh" }}>
                <Spin size="default" />
            </div>
        );
    }

    if (employeeError) {
        return <div className="page-wrapper"><div className="content"><div className="alert alert-danger" role="alert">{employeeError}</div><button className="btn btn-primary" onClick={() => setReload(value => value + 1)}>Retry</button></div></div>;
    }

    if (!apiData) {
        return (
            <div className="content container-fluid mt-3">
                <div className="alert alert-info border-0 shadow-sm" style={{ fontSize: '13px', padding: '12px' }}>
                    <i className="ti ti-info-circle me-2"></i>No vacation details found.
                </div>
            </div>
        );
    }

    if (!isAdmin && !isCurrentApprover && !isAssociatedApprover) {
        if (currentUserId && dataUserId && currentUserId !== dataUserId) {
            return (
                <div className="page-wrapper">
                    <div className="content container-fluid mt-3">
                        <div className="alert alert-danger shadow-sm border-0 d-flex align-items-center gap-2">
                            <i className="ti ti-lock fs-4 text-danger"></i>
                            <div>
                                <strong>Unauthorized Access</strong>
                                <div className="small">You do not have permission to view or modify this application.</div>
                            </div>
                        </div>
                        <button onClick={() => navigate(routes.staffQuarter.vacationIntimation, { state: { activeTab: "INTIMATION_FORM" } })} className="btn btn-primary mt-2">Go Back</button>
                    </div>
                </div>
            );
        }
    }

    const formatDate = (date: any, includeTime: boolean = false) => {
        if (!date) return "-";
        const d = new Date(date);
        if (isNaN(d.getTime())) return date;
        const day = String(d.getDate()).padStart(2, "0");
        const month = d.toLocaleString('default', { month: 'short' });
        const year = d.getFullYear();
        const dateStr = `${day} ${month}, ${year}`;

        if (includeTime) {
            const hours = String(d.getHours()).padStart(2, "0");
            const minutes = String(d.getMinutes()).padStart(2, "0");
            return (
                <div className="d-flex align-items-center gap-2">
                    <span style={{ fontSize: '12px' }}>{dateStr}</span>
                    <span className="text-muted small d-flex align-items-center" style={{ fontSize: '11px', borderLeft: '1px solid #dee2e6', paddingLeft: '8px' }}>
                        <i className="ti ti-clock me-1" style={{ fontSize: '10px' }}></i>{hours}:{minutes}
                    </span>
                </div>
            );
        }
        return dateStr;
    };

    const currentStatus = String(apiData?.status || apiData?.approvalStatus || apiData?.currentStatus || apiData?.vacationStatus || apiData?.requestStatus || (apiData?.isApproved ? "APPROVED" : "PENDING")).toUpperCase();
    const isApprovedStatus = ["APPROVED", "CONFIRMED", "COMPLETED", "ACCEPTED", "VACATED"].includes(currentStatus);

    return (
        <>
            <div className="page-wrapper">
                <div className="content">
                    <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
                        <div className="my-auto mb-2">
                            <h3 className="page-title mb-1" style={{ fontSize: '20px' }}>{detailsTitle}</h3>
                            <nav>
                                <ol className="breadcrumb mb-0">
                                    <li className="breadcrumb-item">
                                        <Link to={isAdmin ? routes.staffQuarterAdmin.dashboard : routes.employeeDashboard}>Dashboard</Link>
                                    </li>
                                    <li className="breadcrumb-item">
                                        <Link to={isAdmin ? routes.staffQuarterAdmin.requests : routes.staffQuarter.vacationIntimation}>Vacations</Link>
                                    </li>
                                    <li className="breadcrumb-item active" aria-current="page">Details</li>
                                </ol>
                            </nav>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                            <button
                                className={`btn btn-sm d-flex align-items-center gap-1 shadow-xs fw-bold ${isApprovedStatus ? 'btn-danger' : 'btn-secondary opacity-50'}`}
                                title={isApprovedStatus ? "Download Vacation Letter" : "Vacation letter is available only after approval"}
                                disabled={!isApprovedStatus}
                                onClick={() => isApprovedStatus && handleDownloadVacationLetter()}
                            >
                                <i className="ti ti-file-download"></i> Download Vacation Letter
                            </button>
                            <button className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1" onClick={() => navigate(routes.staffQuarter.vacationIntimation, { state: { activeTab: backTab } })}>
                                <i className="ti ti-arrow-left"></i> Back
                            </button>
                        </div>
                    </div>

                    {employeeData && <div className="card mb-3"><div className="card-body">
                        <label className="form-label" htmlFor="vacation-intimation-history">{isVacationRequest ? "Vacation Request History" : "Vacation Intimation History"} ({employeeData.totalRecords ?? employeeData.items.length} records)</label>
                        <select id="vacation-intimation-history" className="form-select" value={selectedIndex} onChange={event => setSelectedIndex(Number(event.target.value))}>
                            {employeeData.items.map((item: any, index: number) => <option key={item.vacationId ?? item.id ?? index} value={index}>{item.vacationIntimationId || item.id} — {item.status} — {item.vacationIntimationDate || item.tentativeVacationDate || "-"}</option>)}
                        </select>
                    </div></div>}

                    {hasFinanceData && (
                        <div className="card shadow-sm border-0 mb-3" style={{ borderRadius: '10px' }}>
                            <div className="card-body p-2">
                                <Tabs
                                    activeKey={activeTabKey}
                                    onChange={(key) => setActiveTabKey(key)}
                                    className="custom-nav-tabs"
                                    items={[
                                        {
                                            key: "vacation",
                                            label: (
                                                <div className="d-flex align-items-center gap-2 px-2 py-1">
                                                    <i className="ti ti-plane-departure fs-5"></i>
                                                    <span className="fw-bold">Vacation Request</span>
                                                </div>
                                            ),
                                        },
                                        {
                                            key: "finance",
                                            label: (
                                                <div className="d-flex align-items-center gap-2 px-2 py-1">
                                                    <i className="ti ti-receipt-tax fs-5"></i>
                                                    <span className="fw-bold">Finance</span>
                                                    <Tag color={(financeObj?.status || "").toUpperCase() === "APPROVED" ? "success" : (financeObj?.status || "").toUpperCase() === "REJECTED" ? "error" : "warning"} className="m-0 ms-1 fw-bold">
                                                        {financeObj?.status?.toUpperCase() || "PENDING"}
                                                    </Tag>
                                                </div>
                                            ),
                                        }
                                    ]}
                                />
                            </div>
                        </div>
                    )}

                    {(activeTabKey === "vacation" || !hasFinanceData) && (
                        <>
                            {isVacationRequest && <div className="mb-3 pt-1">
                                {pendingApprover && <div className="alert alert-warning py-2 mb-3">
                                    <strong>Pending with: </strong>{pendingApprover.approverName || pendingApprover.name || pendingApprover.approverId || pendingApprover.empId || String(pendingApprover)}
                                    {(pendingApprover.approverId || pendingApprover.empId) && ` (Emp No: ${pendingApprover.approverId || pendingApprover.empId})`}
                                </div>}
                                <ApproverTracker approvers={trackerApprovers} />
                            </div>}

                            <div className="row">
                                <div className="col-12 mx-auto" style={{ maxWidth: '1100px' }}>
                                    <div className="card mb-3 shadow-sm border-0 overflow-hidden">
                                        <div className="card-header bg-white py-2">
                                            <span className="fw-bold text-muted small text-uppercase">Employee Profile (Read Only)</span>
                                        </div>
                                        <div className="card-body p-3">
                                            <div className="d-flex align-items-center gap-3 p-3 rounded-3" style={{ background: 'linear-gradient(to right, #f8f9fa, #ffffff)', border: '1px solid #eef2f6' }}>
                                                <div className="position-relative">
                                                    <Avatar size={64} src={ensureBase64(employeeImage)} className="border border-2 border-white shadow-sm">
                                                        {String(employeeName).charAt(0) || <i className="ti ti-user fs-3"></i>}
                                                    </Avatar>
                                                </div>
                                                <div className="flex-grow-1">
                                                    <div className="d-flex justify-content-between align-items-start">
                                                        <div>
                                                            <h4 className="mb-1 fw-bold text-dark" style={{ fontSize: '18px' }}>{employeeName}</h4>
                                                            <div className="d-flex align-items-center gap-2">
                                                                <span className="badge bg-primary-transparent text-primary fw-bold" style={{ fontSize: '11px', padding: '4px 10px' }}>
                                                                    {employeeNumber}
                                                                </span>
                                                                <span className="text-muted small" style={{ fontSize: '12px' }}>
                                                                    <i className="ti ti-id-badge me-1"></i> {employeeDesignation}
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <div className="text-end">
                                                            <div className="text-muted extra-small fw-bold text-uppercase mb-1" style={{ fontSize: '9px', letterSpacing: '0.5px' }}>Current Status</div>
                                                            <Tag color={getStatusColor(apiData.status)} className="m-0 px-3 py-1 rounded-pill fw-bold border-0 shadow-sm" style={{ fontSize: '11px' }}>
                                                                {apiData.status?.toUpperCase()}
                                                            </Tag>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="card mb-3 shadow-sm border-0">
                                        <div className="card-header bg-white py-2">
                                            <span className="fw-bold text-muted small text-uppercase">Accommodation Details</span>
                                        </div>
                                        <div className="card-body py-3">
                                            <div className="row">
                                                <div className="col-md-3 mb-2">
                                                    <label className="form-label small fw-bold text-muted mb-1">Flat Number & Type</label>
                                                    <input className="form-control form-control-sm" disabled style={{ backgroundColor: "#f8f9fa", color: "#333", cursor: "not-allowed", fontSize: '13px' }} value={`${formatFlatNoWithBlock(apiData) || apiData.flatNumber || "-"} (${apiData.quarterType || "-"})`} />
                                                </div>
                                                <div className="col-md-3 mb-2">
                                                    <label className="form-label small fw-bold text-muted mb-1">Category</label>
                                                    <input className="form-control form-control-sm" disabled style={{ backgroundColor: "#f8f9fa", color: "#333", cursor: "not-allowed", fontSize: '13px' }} value={resolveFlatCategory(apiData, staffQuarterState?.vacationIntimationData, staffQuarterState?.allVacations)} />
                                                </div>
                                                <div className="col-md-3 mb-2">
                                                    <label className="form-label small fw-bold text-muted mb-1">Station / Location</label>
                                                    <input className="form-control form-control-sm" disabled style={{ backgroundColor: "#f8f9fa", color: "#333", cursor: "not-allowed", fontSize: '13px' }} value={apiData.location || "-"} />
                                                </div>
                                                <div className="col-md-3 mb-2">
                                                    <label className="form-label small fw-bold text-muted mb-1">Allotment Pool</label>
                                                    <input className="form-control form-control-sm" disabled style={{ backgroundColor: "#f8f9fa", color: "#333", cursor: "not-allowed", fontSize: '13px' }} value={apiData.pool || "-"} />
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="card mb-3 shadow-sm border-0">
                                        <div className="card-header bg-white py-2 text-warning">
                                            <span className="fw-bold small text-uppercase">Vacation Request Information</span>
                                        </div>
                                        <div className="card-body py-3">
                                            <div className="row">
                                                <div className="col-md-6 mb-3">
                                                    <label className="form-label small fw-bold text-muted mb-1">
                                                        Vacation Date
                                                    </label>
                                                    <div className="p-2 border rounded bg-light text-dark fw-bold" style={{ fontSize: '13px' }}>
                                                        <i className="ti ti-calendar me-2 text-warning"></i>
                                                        {formatDate(getVacationIntimationDate() || apiData?.vacationIntimationDate || financeObj?.vacationIntimationDate)}
                                                    </div>
                                                </div>
                                                <div className="col-md-6 mb-3">
                                                    <label className="form-label small fw-bold text-muted mb-1">Application Date & Time</label>
                                                    <div className="p-2 border rounded bg-light text-dark" style={{ fontSize: '13px' }}>
                                                        {formatDate(apiData.createdAt, true)}
                                                    </div>
                                                </div>
                                                <div className="col-md-6 mb-3">
                                                    <label className="form-label small fw-bold text-muted mb-1">Registration Reference</label>
                                                    <input className="form-control form-control-sm" disabled style={{ backgroundColor: "#f8f9fa", fontSize: '13px' }} value={apiData.registrationId || apiData.registrationReferenceNumber || "-"} />
                                                </div>
                                                <div className="col-md-6 mb-3">
                                                    <label className="form-label small fw-bold text-muted mb-1">Allotment Reference</label>
                                                    <input className="form-control form-control-sm" disabled style={{ backgroundColor: "#f8f9fa", fontSize: '13px' }} value={apiData.allotmentReferenceNumber || "-"} />
                                                </div>
                                                <div className="col-md-6 mt-2">
                                                    <label className="form-label small fw-bold text-muted mb-1">Reason for Vacation</label>
                                                    <div className="p-3 bg-light rounded border text-dark" style={{ minHeight: '60px', fontSize: '13px', lineHeight: '1.5', wordBreak: 'break-word' }}>
                                                        {apiData.reasonForVacation || "No reason provided."}
                                                    </div>
                                                </div>
                                                <div className="col-md-6 mt-2">
                                                    <label className="form-label small fw-bold text-muted mb-1">Applicant Remarks</label>
                                                    <div className="p-3 bg-light rounded border text-dark" style={{ minHeight: '60px', fontSize: '13px', lineHeight: '1.5', wordBreak: 'break-word' }}>
                                                        {apiData.remarks || ""}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    {/* Vacation Approval Flow & Remarks */}
                                    {approversList.some((appr: any) => {
                                        const rem = appr.remarks || appr.approverRemarks;
                                        return rem && String(rem).trim() !== "" && String(rem).toLowerCase() !== "null";
                                    }) && (
                                            <div className="card mb-3 shadow-sm border-0">
                                                <div className="card-header bg-white border-bottom py-3">
                                                    <h5 className="mb-0 text-dark small fw-bold text-uppercase" style={{ letterSpacing: '0.5px' }}>
                                                        Vacation Approver Remarks
                                                    </h5>
                                                </div>
                                                <div className="card-body p-0">
                                                    <div className="p-3 bg-white">
                                                        {approversList
                                                            .filter((appr: any) => {
                                                                const rem = appr.remarks || appr.approverRemarks;
                                                                return rem && String(rem).trim() !== "" && String(rem).toLowerCase() !== "null";
                                                            })
                                                            .map((appr: any, index: number) => (
                                                                <div key={index} className="mb-2">
                                                                    <div className="card border-0 shadow-none mb-0" style={{ borderRadius: '8px', border: '1px solid #f1f5f9', backgroundColor: '#fcfdfe' }}>
                                                                        <div className="card-body p-2 px-3">
                                                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                                                <div className="d-flex align-items-center">
                                                                                    <div
                                                                                        className="bg-white rounded-circle d-flex align-items-center justify-content-center me-2 overflow-hidden shadow-sm"
                                                                                        style={{ width: '38px', height: '38px', border: '1px solid #e2e8f0' }}
                                                                                    >
                                                                                        {appr.approverImage ? (
                                                                                            <img
                                                                                                src={ensureBase64(appr.approverImage)}
                                                                                                alt={appr.approverName}
                                                                                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                                                            />
                                                                                        ) : (
                                                                                            <i className="ti ti-user text-muted" style={{ fontSize: '16px' }}></i>
                                                                                        )}
                                                                                    </div>
                                                                                    <div>
                                                                                        <div className="fw-bold text-dark" style={{ fontSize: '13.5px' }}>
                                                                                            {appr.approverName || `Approver L${appr.approvalLevel}`}
                                                                                            <span className="text-muted fw-normal small ms-1" style={{ fontSize: '11px' }}>({appr.approverId})</span>
                                                                                        </div>
                                                                                        <div className="text-muted extra-small" style={{ fontSize: '11px' }}>
                                                                                            {appr.approvedAt || appr.actionDate || appr.updatedAt ? formatDate(appr.approvedAt || appr.actionDate || appr.updatedAt, true) : "Awaiting response"}
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                                <div className="text-end">
                                                                                    <span
                                                                                        className={`badge ${appr.status?.toUpperCase() === 'APPROVED'
                                                                                            ? 'bg-success-light text-success'
                                                                                            : (appr.status?.toUpperCase() === 'PENDING' || !appr.status)
                                                                                                ? 'bg-warning-light text-warning'
                                                                                                : 'bg-danger-light text-danger'
                                                                                            }`}
                                                                                        style={{ fontSize: '10px', letterSpacing: '0.5px' }}
                                                                                    >
                                                                                        {appr.status || "PENDING"}
                                                                                    </span>
                                                                                </div>
                                                                            </div>
                                                                            <div
                                                                                className="p-2 px-3 rounded-2"
                                                                                style={{ backgroundColor: '#fff', border: '1px solid #edf2f7', borderLeft: `3px solid ${appr.status?.toUpperCase() === 'REJECTED' ? '#e53e3e' : '#38a169'}` }}
                                                                            >
                                                                                <div className="d-flex gap-2 align-items-start">
                                                                                    <i className={`ti ti-quote ${appr.status?.toUpperCase() === 'REJECTED' ? 'text-danger' : 'text-success'} opacity-50`} style={{ fontSize: '14px', marginTop: '3px' }}></i>
                                                                                    <div className="text-secondary" style={{ fontSize: '13px', lineHeight: '1.5', wordBreak: 'break-word' }}>
                                                                                        {appr.remarks || appr.approverRemarks}
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                    </div>
                                                </div>
                                            </div>
                                        )}


                                    {/* Approver Actions Section - Bottom Placement */}
                                    {isCurrentApprover && apiData.status === "PENDING" && (
                                        <div className="card shadow-sm border-0 mb-4" style={{ background: '#fff9f9' }}>
                                            <div className="card-body p-4 text-center">
                                                <div className="d-flex justify-content-end gap-3">
                                                    <button
                                                        className="btn btn-danger px-4 d-flex align-items-center gap-2 shadow-sm"
                                                        style={{ borderRadius: '6px', fontWeight: 600, fontSize: '13px' }}
                                                        onClick={() => { setActionType("Reject"); setIsActionModalOpen(true); }}
                                                    >
                                                        Reject Vacation
                                                    </button>
                                                    <button
                                                        className="btn btn-success px-4 d-flex align-items-center gap-2 shadow-sm"
                                                        style={{ borderRadius: '6px', fontWeight: 600, fontSize: '13px' }}
                                                        onClick={() => { setActionType("Approve"); setIsActionModalOpen(true); }}
                                                    >
                                                        Approve Vacation
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </>
                    )}

                    {activeTabKey === "finance" && hasFinanceData && (
                        <>
                            <div className="mb-3 pt-1">
                                {pendingFinanceApprover && <div className="alert alert-warning py-2 mb-3">
                                    <strong>Pending with Finance Approver: </strong>{pendingFinanceApprover.approverName || pendingFinanceApprover.approverId}
                                    {(pendingFinanceApprover.approverId || pendingFinanceApprover.empId) && ` (Emp No: ${pendingFinanceApprover.approverId || pendingFinanceApprover.empId})`}
                                </div>}
                                <ApproverTracker approvers={trackerFinanceApprovers} />
                            </div>

                            <div className="row">
                                <div className="col-12 mx-auto" style={{ maxWidth: '1100px' }}>
                                    {/* Employee Profile */}
                                    <div className="card mb-3 shadow-sm border-0 overflow-hidden">
                                        <div className="card-header bg-white py-2">
                                            <span className="fw-bold text-muted small text-uppercase">Employee Profile (Read Only)</span>
                                        </div>
                                        <div className="card-body p-3">
                                            <div className="d-flex align-items-center gap-3 p-3 rounded-3" style={{ background: 'linear-gradient(to right, #f8f9fa, #ffffff)', border: '1px solid #eef2f6' }}>
                                                <div className="position-relative">
                                                    <Avatar size={64} src={ensureBase64(employeeImage)} className="border border-2 border-white shadow-sm">
                                                        {String(employeeName).charAt(0) || <i className="ti ti-user fs-3"></i>}
                                                    </Avatar>
                                                </div>
                                                <div className="flex-grow-1">
                                                    <div className="d-flex justify-content-between align-items-start">
                                                        <div>
                                                            <h4 className="mb-1 fw-bold text-dark" style={{ fontSize: '18px' }}>{employeeName}</h4>
                                                            <div className="d-flex align-items-center gap-2">
                                                                <span className="badge bg-primary-transparent text-primary fw-bold" style={{ fontSize: '11px', padding: '4px 10px' }}>
                                                                    {employeeNumber}
                                                                </span>
                                                                <span className="text-muted small" style={{ fontSize: '12px' }}>
                                                                    <i className="ti ti-id-badge me-1"></i> {employeeDesignation}
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <div className="text-end">
                                                            <div className="text-muted extra-small fw-bold text-uppercase mb-1" style={{ fontSize: '9px', letterSpacing: '0.5px' }}>Finance Status</div>
                                                            <Tag color={(financeObj?.status || "").toUpperCase() === "APPROVED" ? "success" : (financeObj?.status || "").toUpperCase() === "REJECTED" ? "error" : "warning"} className="m-0 px-3 py-1 rounded-pill fw-bold border-0 shadow-sm" style={{ fontSize: '11px' }}>
                                                                {financeObj?.status?.toUpperCase() || "PENDING"}
                                                            </Tag>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Financial Details Card */}
                                    <div className="card mb-3 shadow-sm border-0">
                                        <div className="card-header bg-white py-2 d-flex align-items-center justify-content-between">
                                            <span className="fw-bold text-primary small text-uppercase">Financial Information & Charges</span>
                                            {financeObj?.id && <span className="text-muted small">Finance ID: #{financeObj.id}</span>}
                                        </div>
                                        <div className="card-body py-3">
                                            <div className="row g-3">
                                                <div className="col-md-3 col-sm-6">
                                                    <label className="form-label small fw-bold text-muted mb-1">Request Type</label>
                                                    <input className="form-control form-control-sm" disabled style={{ backgroundColor: "#f8f9fa", fontSize: '13px' }} value={financeObj?.requestType || "VACATION"} />
                                                </div>
                                                <div className="col-md-3 col-sm-6">
                                                    <label className="form-label small fw-bold text-muted mb-1">Vacation Date</label>
                                                    <div className="p-2 border rounded bg-light text-dark fw-bold" style={{ fontSize: '13px' }}>
                                                        <i className="ti ti-calendar me-2 text-warning"></i>
                                                        {formatDate(financeObj?.vacationDate || apiData?.vacationDate)}
                                                    </div>
                                                </div>
                                                <div className="col-md-3 col-sm-6">
                                                    <label className="form-label small fw-bold text-muted mb-1">Vacation Intimation Date</label>
                                                    <div className="p-2 border rounded bg-light text-dark fw-bold" style={{ fontSize: '13px' }}>
                                                        <i className="ti ti-calendar me-2 text-info"></i>
                                                        {formatDate(getVacationIntimationDate() || apiData?.vacationIntimationDate || financeObj?.vacationIntimationDate)}
                                                    </div>
                                                </div>
                                                <div className="col-md-3 col-sm-6">
                                                    <label className="form-label small fw-bold text-muted mb-1">License Fees</label>
                                                    <input className="form-control form-control-sm fw-bold text-primary" disabled style={{ backgroundColor: "#f8f9fa", fontSize: '13px' }} value={financeObj?.licenseFees != null ? `₹${Number(financeObj.licenseFees).toLocaleString('en-IN')}` : "-"} />
                                                </div>
                                                <div className="col-md-3 col-sm-6">
                                                    <label className="form-label small fw-bold text-muted mb-1">License Type</label>
                                                    <input className="form-control form-control-sm" disabled style={{ backgroundColor: "#f8f9fa", fontSize: '13px' }} value={financeObj?.licenseType || "-"} />
                                                </div>
                                                <div className="col-md-3 col-sm-6">
                                                    <label className="form-label small fw-bold text-muted mb-1">Short Fall Days</label>
                                                    <input className="form-control form-control-sm fw-bold text-warning" disabled style={{ backgroundColor: "#f8f9fa", fontSize: '13px' }} value={rawShortfallDays != null ? `${rawShortfallDays} Days` : "-"} />
                                                </div>
                                                <div className="col-md-3 col-sm-6">
                                                    <label className="form-label small fw-bold text-muted mb-1">Recovery Amount</label>
                                                    <input className="form-control form-control-sm fw-bold text-danger" disabled style={{ backgroundColor: "#f8f9fa", fontSize: '13px' }} value={rawRecoveryAmount != null ? `₹${Number(rawRecoveryAmount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "-"} />
                                                </div>
                                                <div className="col-md-3 col-sm-6">
                                                    <label className="form-label small fw-bold text-muted mb-1">Application Date & Time</label>
                                                    <div className="p-2 border rounded bg-light text-dark" style={{ fontSize: '13px' }}>
                                                        {formatDate(financeObj?.createdAt || apiData?.createdAt, true)}
                                                    </div>
                                                </div>
                                                <div className="col-md-12 col-sm-12">
                                                    <label className="form-label small fw-bold text-muted mb-1">Finance Remarks</label>
                                                    <div className="p-2 border rounded bg-light text-dark" style={{ fontSize: '13px', minHeight: '38px' }}>
                                                        {financeObj?.remarks || "No finance remarks provided."}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Finance Approver Remarks */}
                                    {financeApproversList.some((appr: any) => {
                                        const rem = appr.remarks || appr.approverRemarks;
                                        return rem && String(rem).trim() !== "" && String(rem).toLowerCase() !== "null";
                                    }) && (
                                            <div className="card mb-3 shadow-sm border-0">
                                                <div className="card-header bg-white border-bottom py-3">
                                                    <h5 className="mb-0 text-dark small fw-bold text-uppercase" style={{ letterSpacing: '0.5px' }}>
                                                        Finance Approver Remarks
                                                    </h5>
                                                </div>
                                                <div className="card-body p-0">
                                                    <div className="p-3 bg-white">
                                                        {financeApproversList
                                                            .filter((appr: any) => {
                                                                const rem = appr.remarks || appr.approverRemarks;
                                                                return rem && String(rem).trim() !== "" && String(rem).toLowerCase() !== "null";
                                                            })
                                                            .map((appr: any, index: number) => (
                                                                <div key={index} className="mb-2">
                                                                    <div className="card border-0 shadow-none mb-0" style={{ borderRadius: '8px', border: '1px solid #f1f5f9', backgroundColor: '#fcfdfe' }}>
                                                                        <div className="card-body p-2 px-3">
                                                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                                                <div className="d-flex align-items-center">
                                                                                    <div
                                                                                        className="bg-white rounded-circle d-flex align-items-center justify-content-center me-2 overflow-hidden shadow-sm"
                                                                                        style={{ width: '38px', height: '38px', border: '1px solid #e2e8f0' }}
                                                                                    >
                                                                                        {appr.approverImage ? (
                                                                                            <img
                                                                                                src={ensureBase64(appr.approverImage)}
                                                                                                alt={appr.approverName}
                                                                                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                                                            />
                                                                                        ) : (
                                                                                            <i className="ti ti-user text-muted" style={{ fontSize: '16px' }}></i>
                                                                                        )}
                                                                                    </div>
                                                                                    <div>
                                                                                        <div className="fw-bold text-dark" style={{ fontSize: '13.5px' }}>
                                                                                            {appr.approverName || `Finance Approver ${appr.approvedByRole || ''}`}
                                                                                            <span className="text-muted fw-normal small ms-1" style={{ fontSize: '11px' }}>({appr.approverId})</span>
                                                                                        </div>
                                                                                        <div className="text-muted extra-small" style={{ fontSize: '11px' }}>
                                                                                            {appr.approvedAt || appr.actionDate || appr.updatedAt ? formatDate(appr.approvedAt || appr.actionDate || appr.updatedAt, true) : "Awaiting response"}
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                                <div className="text-end">
                                                                                    <span
                                                                                        className={`badge ${appr.status?.toUpperCase() === 'APPROVED'
                                                                                            ? 'bg-success-light text-success'
                                                                                            : (appr.status?.toUpperCase() === 'PENDING' || !appr.status)
                                                                                                ? 'bg-warning-light text-warning'
                                                                                                : 'bg-danger-light text-danger'
                                                                                            }`}
                                                                                        style={{ fontSize: '10px', letterSpacing: '0.5px' }}
                                                                                    >
                                                                                        {appr.status || "PENDING"}
                                                                                    </span>
                                                                                </div>
                                                                            </div>
                                                                            <div
                                                                                className="p-2 px-3 rounded-2"
                                                                                style={{ backgroundColor: '#fff', border: '1px solid #edf2f7', borderLeft: `3px solid ${appr.status?.toUpperCase() === 'REJECTED' ? '#e53e3e' : '#38a169'}` }}
                                                                            >
                                                                                <div className="d-flex gap-2 align-items-start">
                                                                                    <i className={`ti ti-quote ${appr.status?.toUpperCase() === 'REJECTED' ? 'text-danger' : 'text-success'} opacity-50`} style={{ fontSize: '14px', marginTop: '3px' }}></i>
                                                                                    <div className="text-secondary" style={{ fontSize: '13px', lineHeight: '1.5', wordBreak: 'break-word' }}>
                                                                                        {appr.remarks || appr.approverRemarks}
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                    {/* Finance Approver Actions */}
                                    {isCurrentFinanceApprover && (financeObj?.status || "").toUpperCase() === "PENDING" && (
                                        <div className="card shadow-sm border-0 mb-4" style={{ background: '#f8fafc' }}>
                                            <div className="card-body p-4 text-center">
                                                <div className="d-flex justify-content-end gap-3">
                                                    <button
                                                        className="btn btn-danger px-4 d-flex align-items-center gap-2 shadow-sm"
                                                        style={{ borderRadius: '6px', fontWeight: 600, fontSize: '13px' }}
                                                        onClick={() => { setFinanceActionType("Reject"); setIsFinanceModalOpen(true); }}
                                                    >
                                                        Reject Finance
                                                    </button>
                                                    <button
                                                        className="btn btn-success px-4 d-flex align-items-center gap-2 shadow-sm"
                                                        style={{ borderRadius: '6px', fontWeight: 600, fontSize: '13px' }}
                                                        onClick={() => { setFinanceActionType("Approve"); setIsFinanceModalOpen(true); }}
                                                    >
                                                        Approve Finance
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </>
                    )}

                    <Modal
                        title={
                            <div className="d-flex align-items-center gap-2">
                                <i className={`ti ti-${actionType === 'Approve' ? 'check' : 'x'} text-${actionType === 'Approve' ? 'success' : 'danger'} fs-5`}></i>
                                <span className="fw-bold" style={{ fontSize: '15px' }}>{actionType} Request</span>
                            </div>
                        }
                        open={isActionModalOpen}
                        onCancel={() => setIsActionModalOpen(false)}
                        onOk={() => form.submit()}
                        okText={actionType}
                        okButtonProps={{ danger: actionType === "Reject", size: 'small' }}
                        cancelButtonProps={{ size: 'small' }}
                        centered
                        width={400}
                    >
                        <Form form={form} onFinish={handleAction} layout="vertical" className="mt-2">
                            <Form.Item
                                name="remarks"
                                label={<span className="fw-bold small text-muted">Administrative Remarks</span>}
                                rules={[{ required: true, message: "Required" }]}
                            >
                                <Input.TextArea rows={3} placeholder="Enter details..." style={{ fontSize: '13px' }} />
                            </Form.Item>
                        </Form>
                    </Modal>

                    <Modal
                        title={
                            <div className="d-flex align-items-center gap-2">
                                <i className={`ti ti-${financeActionType === 'Approve' ? 'check' : 'x'} text-${financeActionType === 'Approve' ? 'success' : 'danger'} fs-5`}></i>
                                <span className="fw-bold" style={{ fontSize: '15px' }}>{financeActionType} Finance Request</span>
                            </div>
                        }
                        open={isFinanceModalOpen}
                        onCancel={() => setIsFinanceModalOpen(false)}
                        onOk={() => financeForm.submit()}
                        okText={financeActionType}
                        okButtonProps={{ danger: financeActionType === "Reject", size: 'small' }}
                        cancelButtonProps={{ size: 'small' }}
                        centered
                        width={450}
                    >
                        <Form form={financeForm} onFinish={handleFinanceActionSubmit} layout="vertical" className="mt-2">
                            <Form.Item
                                name="remarks"
                                label={<span className="fw-bold small text-muted">Finance Remarks</span>}
                                rules={[{ required: true, message: "Required" }]}
                            >
                                <Input.TextArea rows={3} placeholder="Enter finance approval remarks..." style={{ fontSize: '13px' }} />
                            </Form.Item>
                        </Form>
                    </Modal>
                </div>

                <style>{`
                .bg-primary-transparent { background: rgba(227, 6, 19, 0.04); }
                .bg-info-transparent { background: rgba(13, 202, 240, 0.04); }
                .bg-warning-transparent { background: rgba(255, 193, 7, 0.04); }
                .shadow-xs { box-shadow: 0 1px 2px rgba(0,0,0,0.05); }
                .approver-tracker-compact .events-status-header { display: none; }
                .ant-card-head { min-height: 32px !important; }
            `}</style>
            </div>
        </>
    );
};

export default VacationDetails;
